import { provideHttpClient } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
} from '@angular/core';
import { COMPOSITION_BUFFER_MODE } from '@angular/forms';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideFuse } from '@fuse/fuse.provider';
import { DialogModule } from '@libs/shared/ui/dialogs/dialog.module';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { appRoutes } from '@sprint-backoffice/app/app.routes';
import { provideApi } from '@sprint-backoffice/app/core/api/api.provider';
import { provideAuth } from '@sprint-backoffice/app/core/auth/auth.provider';
import { provideIcons } from '@sprint-backoffice/app/core/icons/icons.provider';
import { provideCoreFeature } from '@sprint-backoffice/app/core/state/core.store';
import { TranslocoHttpLoader } from '@sprint-backoffice/app/core/transloco/transloco.http-loader';
import { mockApiServices } from '@sprint-backoffice/app/mock-api';
import { provideNgrxStore } from '@sprint-backoffice/app/state/state.provider';
import { environment } from '@sprint-backoffice/environments/environment';
import { IMAGE_RESIZE_URL } from '@libs/shared/tokens/image-resize-url.token';
import { firstValueFrom } from 'rxjs';

export const appConfig: ApplicationConfig = {
  providers: [
    provideEffects(),
    provideStore(),
    provideStoreDevtools(),
    provideCoreFeature(),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        // enabled: !isDevMode(),
        enabled: !isDevMode(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      MatSnackBarModule,
      DialogModule
    ),
    provideHttpClient(),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideApi(),
    provideAuth(),
    provideNgrxStore(),
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'ko',
            label: '한국어',
          },
        ],
        defaultLang: 'ko',
        fallbackLang: 'ko',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(defaultLang);

        return () => firstValueFrom(translocoService.load(defaultLang));
      },
      multi: true,
    },
    provideIcons(),
    {
      provide: COMPOSITION_BUFFER_MODE,
      useValue: false,
    },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'yyyy-MM-dd',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    {
      provide: 'baseS3Path',
      useValue: environment.s3Path,
    },
    {
      provide: IMAGE_RESIZE_URL,
      useValue: environment.imageResizeUrl,
    },
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: 'dense',
        scheme: 'auto',
        screens: {
          sm: '600px',
          md: '768px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-purple',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
